// Columna central de Clientes: píldoras, conteo, la tabla del mock y «Cargar más».
import { useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { ACCOUNT_ONLY_LABEL } from '@/shared/lib/format/accountOnly';
import { formatPhone } from '@/shared/lib/format/phone';
import { DataTable, UnavailableState, type DataTableColumn } from '@/shared/patterns/DataTable';
import { StatusPill, TierPill } from '@/shared/patterns/Pills';
import { StatsBar, type StatsBarItem, type StatsBarTone } from '@/shared/patterns/StatsBar';
import {
    CUSTOMERS_NOUN,
    LoadMoreButton,
    loadedCountLabel,
    loadedCountTitle,
    loadedCountsNote,
    loadedResultLabel,
    nextPageFailureNote,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import {
    nextSort,
    storeShownOf,
    type Bucket,
    type BucketCounts,
    type CreditReadState,
    type CustomerListRow,
    type SortKey,
    type SortState,
} from '../lib/customersList';
import {
    ACCOUNT_ONLY_NOTE,
    AGGREGATES_NO_SOURCE,
    daysLabel,
    daysToneClass,
    FAVORITE_STORE_NOTE,
    LEVEL_ERROR,
    LEVEL_FORBIDDEN,
    LEVEL_MISSING,
    moneyWhole,
    REBUY_RULE_MISSING,
    shortId,
    STATUS_HINT,
    STATUS_LABEL,
    STATUS_TONE,
} from '../lib/customersFormat';
import { Missing, WrapLabel } from './cells';

const PILLS: { key: Bucket; label: string; tone: StatsBarTone; rule: string }[] = [
    { key: 'ALL', label: 'Todos', tone: 'neutral', rule: 'Todos los clientes cargados' },
    { key: 'ACTIVE', label: 'Activos', tone: 'success', rule: 'Última compra ≤30d' },
    { key: 'DORMANT', label: 'Dormidos', tone: 'muted', rule: 'Sin comprar >90d' },
    { key: 'REBUY', label: 'Candidatos a recompra', tone: 'warning', rule: REBUY_RULE_MISSING },
    { key: 'RECURRING', label: 'Recurrentes', tone: 'neutral', rule: '≥2 compras' },
    {
        key: 'WITH_CREDIT',
        label: 'Con crédito SK',
        tone: 'primary',
        rule: 'Tiene al menos 1 crédito SK activo',
    },
    { key: 'ACCOUNT_ONLY', label: ACCOUNT_ONLY_LABEL, tone: 'info', rule: ACCOUNT_ONLY_NOTE },
];

/** Filas que se pintan de entrada; el resto se pide con «Mostrar más». */
const RENDER_STEP = 200;

function creditReason(state: CreditReadState): string {
    if (state === 'ready') return LEVEL_MISSING;
    return state === 'forbidden' ? LEVEL_FORBIDDEN : LEVEL_ERROR;
}

function pillUnavailableReason(
    key: Bucket,
    aggregatesReason: string,
    creditState: CreditReadState,
): string {
    if (key === 'REBUY') return REBUY_RULE_MISSING;
    if (key === 'RECURRING') return aggregatesReason;
    return creditState === 'loading' ? 'Leyendo la cartera SureKeep…' : creditReason(creditState);
}

export interface CustomersTableProps {
    /** Las filas que pasan los filtros locales, ya ordenadas. */
    rows: CustomerListRow[];
    /** Filas acumuladas de todas las páginas traídas. */
    loadedCount: number;
    counts: BucketCounts;
    bucket: Bucket;
    onBucketChange: (bucket: Bucket) => void;
    windowFrom: string | null;
    truncated: boolean;
    aggregatesUnavailableReason: string | null;
    creditState: CreditReadState;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    selectedId: string | null;
    onSelect: (retailCustomerId: string) => void;
    isLoading: boolean;
    error: Error | null;
    onRetry: () => void;
    /** Quedan páginas en el servidor. */
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Por qué falló la última página pedida; `null` si no falló. */
    nextPageError: string | null;
}

export function CustomersTable({
    rows,
    loadedCount,
    counts,
    bucket,
    onBucketChange,
    windowFrom,
    truncated,
    aggregatesUnavailableReason,
    creditState,
    sort,
    onSortChange,
    selectedId,
    onSelect,
    isLoading,
    error,
    onRetry,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    nextPageError,
}: CustomersTableProps) {
    // El tope vuelve a su default cuando cambian las filas (otro filtro u otro orden).
    const [expanded, setExpanded] = useState({ rows, visible: RENDER_STEP });
    const visible = expanded.rows === rows ? expanded.visible : RENDER_STEP;

    if (error) {
        return (
            <UnavailableState
                title="No se pudo cargar la lista de clientes"
                body={error.message}
                action={
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        Reintentar
                    </Button>
                }
            />
        );
    }

    const scope: LoadedScope = { loaded: loadedCount, hasMore: hasNextPage };
    const countsNote = isLoading ? null : loadedCountsNote(scope, CUSTOMERS_NOUN);
    const aggregatesReason = aggregatesUnavailableReason ?? AGGREGATES_NO_SOURCE;
    const pills: StatsBarItem<Bucket>[] = PILLS.map((pill) => {
        const value = counts[pill.key];
        const unavailable = value === null;
        return {
            key: pill.key,
            label: pill.label,
            tone: pill.tone,
            disabled: isLoading || unavailable,
            title: unavailable
                ? pillUnavailableReason(pill.key, aggregatesReason, creditState)
                : loadedCountTitle(pill.rule, scope, CUSTOMERS_NOUN),
            value: isLoading || unavailable ? '—' : loadedCountLabel(value, scope),
        };
    });

    const aggregateCell = (row: CustomerListRow, cents: number | null): ReactNode => {
        if (row.accountOnly) return '—';
        return cents === null ? <Missing reason={aggregatesReason} /> : moneyWhole(cents);
    };

    const columns: DataTableColumn<CustomerListRow, SortKey>[] = [
        {
            key: 'id',
            label: 'ID',
            width: '56px',
            sortKey: 'id',
            cellTitle: (row) => row.retailCustomerId,
            cellClassName: () => 'font-mono text-[9.5px] text-muted-foreground',
            cell: (row) => shortId(row.retailCustomerId),
        },
        {
            key: 'name',
            label: 'Nombre',
            sortKey: 'name',
            cellTitle: (row) =>
                [row.name, formatPhone(row.phone)].filter(Boolean).join(' · ') || undefined,
            cell: (row) =>
                row.name ? (
                    <span className="font-semibold">{row.name}</span>
                ) : (
                    <Missing reason="La venta no congeló el nombre." />
                ),
        },
        {
            key: 'status',
            label: 'Estado',
            width: '64px',
            sortKey: 'status',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.status === null ? (
                    '—'
                ) : (
                    <StatusPill tone={STATUS_TONE[row.status]} title={STATUS_HINT[row.status]}>
                        {STATUS_LABEL[row.status]}
                    </StatusPill>
                ),
        },
        {
            key: 'level',
            label: <WrapLabel>Nivel SK</WrapLabel>,
            width: '56px',
            sortKey: 'level',
            cellOverflow: 'nowrap',
            cell: (row) =>
                row.credit !== undefined ? (
                    <TierPill level={row.credit.level} />
                ) : creditState === 'loading' ? (
                    '…'
                ) : (
                    <Missing reason={creditReason(creditState)} />
                ),
        },
        {
            key: 'purchases',
            label: 'Compras',
            width: '64px',
            numeric: true,
            sortKey: 'purchases',
            cell: (row) =>
                row.accountOnly ? 0 : (row.purchaseCount ?? <Missing reason={aggregatesReason} />),
        },
        {
            key: 'total',
            label: 'Total $',
            width: '70px',
            numeric: true,
            sortKey: 'total',
            cell: (row) => aggregateCell(row, row.totalSpentCents),
        },
        {
            key: 'ticket',
            label: 'Ticket',
            width: '62px',
            numeric: true,
            sortKey: 'ticket',
            cell: (row) => aggregateCell(row, row.averageTicketCents),
        },
        {
            key: 'days',
            label: <WrapLabel>Días s/compra</WrapLabel>,
            width: '64px',
            numeric: true,
            sortKey: 'days',
            cellTitle: (row) =>
                row.lastPurchaseAt === null
                    ? undefined
                    : `Última compra: ${formatDateMx(row.lastPurchaseAt)}`,
            cellClassName: (row) =>
                row.daysSincePurchase === null ? undefined : daysToneClass(row.daysSincePurchase),
            cell: (row) =>
                row.daysSincePurchase === null ? '—' : daysLabel(row.daysSincePurchase),
        },
        {
            key: 'store',
            label: <WrapLabel>Tienda fav</WrapLabel>,
            note: FAVORITE_STORE_NOTE,
            width: '74px',
            sortKey: 'store',
            cellTitle: (row) => {
                const store = storeShownOf(row);
                if (store === null) return undefined;
                return row.accountOnly ? `Tienda de alta: ${store}` : store;
            },
            cell: (row) => storeShownOf(row) ?? '—',
        },
    ];

    const shownRows = rows.slice(0, visible);
    const hidden = rows.length - shownRows.length;

    return (
        <>
            <StatsBar
                items={pills}
                active={bucket}
                onChange={onBucketChange}
                ariaLabel="Grupos de clientes"
                // Las siete no caben en una fila sin cortarse: van en dos filas, de cuatro y tres.
                className="flex-wrap gap-y-1 [&>button]:basis-[23%]"
            />

            <div className="flex flex-none items-center gap-1.5 border-b border-border/50 px-3 py-1 text-[10.5px] font-semibold text-muted-foreground">
                <span role="status">
                    {isLoading
                        ? '— clientes'
                        : loadedResultLabel(rows.length, scope, CUSTOMERS_NOUN)}
                </span>
                {windowFrom ? (
                    <span
                        className="font-normal"
                        title="La lista trae a quien compró desde esa fecha y a quien abrió su cuenta desde entonces sin comprar. Para ver más atrás, usa el filtro «Última compra»."
                    >
                        · desde {formatDateMx(windowFrom)}
                    </span>
                ) : null}
                {truncated ? (
                    <span
                        className="font-normal text-warning"
                        title="El sistema devolvió un tope de clientes. Acorta el rango de «Última compra» para revisarlos por partes."
                    >
                        · lista incompleta
                    </span>
                ) : null}
            </div>

            {countsNote === null ? null : (
                <p
                    role="status"
                    className="flex-none border-b border-border/50 bg-muted/40 px-3 py-1 text-[10px] leading-snug text-muted-foreground"
                >
                    {countsNote}
                </p>
            )}

            <DataTable
                columns={columns}
                rows={shownRows}
                rowKey={(row) => row.retailCustomerId}
                caption="Clientes con estado, nivel SK, compras, total, ticket, días sin compra y tienda favorita o de alta."
                density="compact"
                sort={sort}
                onSortToggle={(key) => {
                    onSortChange(nextSort(sort, key));
                }}
                isRowSelected={(row) => row.retailCustomerId === selectedId}
                onRowClick={(row) => {
                    onSelect(row.retailCustomerId);
                }}
                isLoading={isLoading}
                empty={
                    <span className="text-[11.5px] italic text-muted-foreground">
                        Sin clientes con los filtros seleccionados.
                    </span>
                }
                className={cn(hidden > 0 && 'pb-1')}
            />

            {hidden > 0 || hasNextPage || nextPageError !== null ? (
                <div className="flex flex-none flex-col items-center gap-1 border-t border-border py-1.5">
                    {nextPageError === null ? null : (
                        <p role="alert" className="px-3 text-[11px] leading-snug text-danger">
                            {nextPageFailureNote(nextPageError, CUSTOMERS_NOUN)}
                        </p>
                    )}
                    {hidden > 0 ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setExpanded({ rows, visible: visible + RENDER_STEP });
                            }}
                        >
                            <RefreshCw className="mr-1 size-3" aria-hidden />
                            Mostrar {Math.min(hidden, RENDER_STEP)} más de{' '}
                            {hidden.toLocaleString('es-MX')}
                        </Button>
                    ) : hasNextPage ? (
                        <LoadMoreButton
                            noun={CUSTOMERS_NOUN}
                            isLoading={isFetchingNextPage}
                            hasFailed={nextPageError !== null}
                            onLoadMore={onLoadMore}
                        />
                    ) : null}
                </div>
            ) : null}
        </>
    );
}
